import React, { useMemo, useState } from "react";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";

import {
  computeProductTotals,
  computeTankMetrics,
  createEmptyProductRow,
  DEFAULT_CALIBRATION_TOLERANCE_PERCENT,
  formatFieldLoadSpreadsheetText,
  formatLocationPrefaceText,
  formatProductSpreadsheetText,
  parsePositiveNumber,
  productFieldLoadRows,
  productSpreadsheetRows,
  textBeforeProductSection,
} from "./serviceOrderProducts";
import { copyOrderText, copyPlainText, ServiceOrderCalibrationAverages } from "./serviceOrderExecutionSummary";
import { isSprayCalibrationServiceType } from "../../constants/serviceOrderTypes";
import "./ServiceOrderProductsPanel.css";

const UNIT_OPTIONS = ["L", "mL", "kg", "g"];

function ServiceOrderProductsPanel({
  applicationArea,
  productRows,
  onProductRowsChange,
  sprayVolumePerHa,
  onSprayVolumePerHaChange,
  targetRateLHa,
  onTargetRateLHaChange,
  calibrationTolerancePercent,
  onCalibrationTolerancePercentChange,
  tankCapacityL,
  onTankCapacityLChange,
  serviceType,
  t,
}) {
  const totalHa = parsePositiveNumber(applicationArea && applicationArea.totalHa);
  const computedRows = useMemo(
    () => computeProductTotals(productRows, totalHa),
    [productRows, totalHa]
  );
  const tankMetrics = useMemo(
    () =>
      computeTankMetrics({
        sprayVolumePerHa,
        tankCapacityL,
        totalHa,
      }),
    [sprayVolumePerHa, tankCapacityL, totalHa]
  );

  function updateRow(rowId, field, value) {
    onProductRowsChange(
      productRows.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row
      )
    );
  }

  function addRow() {
    onProductRowsChange([...productRows, createEmptyProductRow()]);
  }

  function removeRow(rowId) {
    if (productRows.length <= 1) {
      onProductRowsChange([createEmptyProductRow()]);
      return;
    }
    onProductRowsChange(productRows.filter((row) => row.id !== rowId));
  }

  const cleanFenceWidthM = Number(applicationArea && applicationArea.cleanFenceWidthM) || 0;
  const fenceHa = parsePositiveNumber(applicationArea && applicationArea.fenceHa);
  const showCalibrationTarget = isSprayCalibrationServiceType(serviceType);

  return (
    <div className="service-order-products">
      <div className="service-order-products__header">
        <label className="service-order-products__title">
          {t("Produtos a ser utilizados")}
        </label>
        <p className="service-order-products__subtitle">{t("Quantidade por hectare")}</p>
        <p className="create-schedule-hint">
          {t("O total de cada produto é calculado automaticamente pela área selecionada acima.")}
        </p>
        <p className="create-schedule-hint">
          {t("Seguir a ordem de aplicação no tanque. O número 1 entra primeiro.")}
        </p>
      </div>

      <div className="service-order-products__area-box">
        <strong>{t("Área total da aplicação")}</strong>
        {totalHa > 0 ? (
          <div className="service-order-products__area-lines">
            {applicationArea.plotCount > 0 ? (
              <span>
                {applicationArea.plotCount || 0} {t("talhão(ões)")} ·{" "}
                {parsePositiveNumber(applicationArea.workHa).toFixed(2)} ha
              </span>
            ) : null}
            {applicationArea.manualPieceCount > 0 ? (
              <span>
                {applicationArea.manualPieceCount} {t("trecho(s) manual(is)")} ·{" "}
                {parsePositiveNumber(applicationArea.manualHa).toFixed(2)} ha
              </span>
            ) : null}
            {cleanFenceWidthM > 0 && fenceHa > 0 ? (
              <span>
                + Cerca limpa {fenceHa.toFixed(2)} ha @ {cleanFenceWidthM} m
              </span>
            ) : applicationArea.plotCount > 0 ? (
              <span>{t("Cerca limpa")}: {t("não")}</span>
            ) : null}
            <span className="is-total">{totalHa.toFixed(2)} ha</span>
          </div>
        ) : (
          <p className="service-order-products__area-placeholder">
            {t("Selecione os talhões no mapa ou na lista acima para calcular os produtos.")}
          </p>
        )}
      </div>

      <div className="service-order-products__table-wrap">
        <table className="service-order-products__table">
          <thead>
            <tr>
              <th>{t("Ordem")}</th>
              <th>{t("Produto")}</th>
              <th>{t("Dosagem por hectare")}</th>
              <th>{t("Unidade")}</th>
              <th>{t("Dosagem por tanque")}</th>
              <th>{t("Total")}</th>
              <th aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {computedRows.map((row, index) => {
              const perTank =
                tankMetrics.haPerTank > 0 && row.dose > 0
                  ? row.dose * tankMetrics.haPerTank
                  : 0;
              return (
              <tr key={row.id}>
                <td className="service-order-products__order-cell">{index + 1}</td>
                <td>
                  <input
                    type="text"
                    className="form-control input-sm"
                    value={row.name}
                    onChange={(event) => updateRow(row.id, "name", event.target.value)}
                    placeholder={t("Nome do produto")}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    className="form-control input-sm"
                    value={row.dosePerHa}
                    onChange={(event) =>
                      updateRow(row.id, "dosePerHa", event.target.value)
                    }
                    placeholder="0"
                  />
                </td>
                <td>
                  <select
                    className="form-control input-sm"
                    value={row.unit}
                    onChange={(event) => updateRow(row.id, "unit", event.target.value)}
                  >
                    {UNIT_OPTIONS.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="service-order-products__total-cell">
                  {perTank > 0 ? `${perTank.toFixed(2)} ${row.unit}` : "—"}
                </td>
                <td className="service-order-products__total-cell">
                  {row.total > 0
                    ? `${row.total.toFixed(2)} ${row.unit}`
                    : "—"}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-link btn-sm service-order-products__remove"
                    onClick={() => removeRow(row.id)}
                    aria-label={t("Remover produto")}
                  >
                    <FaTrashAlt />
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        className="btn btn-default btn-sm service-order-products__add"
        onClick={addRow}
      >
        <FaPlus style={{ marginRight: 6 }} />
        {t("Adicionar produto")}
      </button>

      <div className="service-order-products__tank-grid">
        <label>
          {t("Vazão de calda por hectare (L/ha)")}
          <input
            type="number"
            min="0"
            step="any"
            className="form-control input-sm"
            value={sprayVolumePerHa}
            onChange={(event) => onSprayVolumePerHaChange(event.target.value)}
            placeholder={t("Ex.: 150")}
          />
        </label>
        {showCalibrationTarget ? (
          <>
            <label>
              {t("Taxa-alvo calibragem (L/ha)")}
              <input
                type="number"
                min="0"
                step="any"
                className="form-control input-sm"
                value={targetRateLHa}
                onChange={(event) => onTargetRateLHaChange(event.target.value)}
                placeholder={
                  parsePositiveNumber(sprayVolumePerHa) > 0
                    ? String(parsePositiveNumber(sprayVolumePerHa))
                    : t("Ex.: 100")
                }
                title={t(
                  "Taxa recomendada no check de calibragem. Por padrão usa a vazão de calda, mas pode ser ajustada."
                )}
              />
            </label>
            <label>
              {t("Tolerância de variação (%)")}
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                className="form-control input-sm"
                value={calibrationTolerancePercent}
                onChange={(event) =>
                  onCalibrationTolerancePercentChange(event.target.value)
                }
                placeholder={String(DEFAULT_CALIBRATION_TOLERANCE_PERCENT)}
                title={t(
                  "Variação acima deste percentual em relação à taxa-alvo gera alerta no app e na plataforma."
                )}
              />
            </label>
          </>
        ) : null}
        <label>
          {t("Capacidade do tanque (L)")}
          <input
            type="number"
            min="0"
            step="any"
            className="form-control input-sm"
            value={tankCapacityL}
            onChange={(event) => onTankCapacityLChange(event.target.value)}
            placeholder={t("Ex.: 3000")}
          />
        </label>
        <label>
          {t("Hectares por tanque")}
          <input
            type="text"
            className="form-control input-sm"
            value={tankMetrics.haPerTank > 0 ? tankMetrics.haPerTank.toFixed(2) : ""}
            readOnly
            placeholder="—"
          />
        </label>
        <label>
          {t("Tanques necessários")}
          <input
            type="text"
            className="form-control input-sm"
            value={
              tankMetrics.tanksRequired > 0 ? tankMetrics.tanksRequired.toFixed(2) : ""
            }
            readOnly
            placeholder="—"
          />
        </label>
        <label>
          {t("Volume total de calda (L)")}
          <input
            type="text"
            className="form-control input-sm"
            value={
              tankMetrics.totalSprayVolumeL > 0
                ? tankMetrics.totalSprayVolumeL.toFixed(0)
                : ""
            }
            readOnly
            placeholder="—"
          />
        </label>
      </div>
    </div>
  );
}

export default ServiceOrderProductsPanel;

export function ServiceOrderProductsWithCopy({ obs, t }) {
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState({ preface: true, tank: true, field: true });
  const plan = obs.service_order_plan;
  const productsUsed = obs.products_used;
  const farmLabel = obs.service_farm_names || obs.site_farm_name;
  const plotNames = obs.land_plot_names;
  const rows = productSpreadsheetRows(plan);
  const preface = rows.length
    ? textBeforeProductSection(productsUsed)
    : String(productsUsed || "").trim();
  const hasBlocks = Boolean(preface || rows.length);

  function startCopy() {
    if (!hasBlocks) {
      copyOrderText(obs, t);
      return;
    }
    setPicked({ preface: true, tank: true, field: true });
    setSelecting(true);
  }

  async function confirmCopy() {
    const parts = [];
    if (picked.preface && preface) {
      parts.push(
        formatLocationPrefaceText({
          farmLabel,
          plotNames,
          preface,
          translate: t,
        })
      );
    }
    if (picked.tank && rows.length) parts.push(formatProductSpreadsheetText(plan, t));
    if (picked.field && rows.length) parts.push(formatFieldLoadSpreadsheetText(plan, t));
    const text = parts.filter(Boolean).join("\n\n");
    if (!text) {
      toast.info(t("Marque ao menos um campo para copiar."), {
        position: "bottom-right",
      });
      return;
    }
    const copied = await copyPlainText(text, t);
    if (copied) setSelecting(false);
  }

  return (
    <>
      <ServiceOrderProductSheet
        plan={plan}
        productsUsed={productsUsed}
        farmLabel={farmLabel}
        plotNames={plotNames}
        t={t}
        selecting={selecting}
        picked={picked}
        onToggle={(key) =>
          setPicked((current) => ({ ...current, [key]: !current[key] }))
        }
      />
      <ServiceOrderCalibrationAverages
        obs={obs}
        t={t}
        selecting={selecting}
        canSelect={hasBlocks}
        onStartCopy={startCopy}
        onConfirmCopy={confirmCopy}
        onCancelCopy={() => setSelecting(false)}
      />
    </>
  );
}

export function ServiceOrderProductSheet({
  plan,
  productsUsed,
  t,
  selecting = false,
  picked = {},
  onToggle,
}) {
  const rows = productSpreadsheetRows(plan);
  const fieldLoadRows = productFieldLoadRows(plan);
  const preface = rows.length
    ? textBeforeProductSection(productsUsed)
    : String(productsUsed || "").trim();
  if (!preface && !rows.length) return null;
  const area = Number(plan && plan.application && plan.application.total_ha) || 0;
  const fieldLoadTitle =
    area > 0
      ? `${t("Produtos a serem levados ao campo")} (${area.toFixed(2)} ha)`
      : t("Produtos a serem levados ao campo");

  return (
    <div className="pending-schedules-card__products">
      {preface ? (
        <PickableBlock
          selecting={selecting}
          checked={picked.preface}
          label={t("Fazenda, talhão e área")}
          onToggle={() => onToggle && onToggle("preface")}
        >
          <div className="service-order-product-sheet__preface">{preface}</div>
        </PickableBlock>
      ) : null}
      {rows.length ? (
        <>
          <PickableBlock
            selecting={selecting}
            checked={picked.tank}
            label={t("Seguir a ordem de aplicação no tanque. O número 1 entra primeiro.")}
            onToggle={() => onToggle && onToggle("tank")}
          >
            <div className="service-order-product-sheet__section">
              <p className="service-order-product-sheet__order-note">
                {t("Seguir a ordem de aplicação no tanque. O número 1 entra primeiro.")}
              </p>
              <div className="service-order-product-sheet__wrap">
                <table className="service-order-product-sheet">
                  <thead>
                    <tr>
                      <th>{t("Ordem")}</th>
                      <th>{t("Produto")}</th>
                      <th>{t("Dosagem por hectare")}</th>
                      <th>{t("Dosagem por tanque")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={`${row.order}-${row.name}`}>
                        <td>{row.order}</td>
                        <td>{row.name}</td>
                        <td>{row.dosePerHa || "—"}</td>
                        <td>{row.dosePerTank || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </PickableBlock>
          <PickableBlock
            selecting={selecting}
            checked={picked.field}
            label={fieldLoadTitle}
            onToggle={() => onToggle && onToggle("field")}
          >
            <div className="service-order-product-sheet__field-load">
              <p className="service-order-product-sheet__order-note">{fieldLoadTitle}</p>
              <div className="service-order-product-sheet__wrap">
                <table className="service-order-product-sheet">
                  <thead>
                    <tr>
                      <th>{t("Ordem")}</th>
                      <th>{t("Produto")}</th>
                      <th>{t("Dosagem por hectare")}</th>
                      <th>{t("Total")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fieldLoadRows.map((row) => (
                      <tr key={`field-${row.order}-${row.name}`}>
                        <td>{row.order}</td>
                        <td>{row.name}</td>
                        <td>{row.dosePerHa || "—"}</td>
                        <td>{row.total || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </PickableBlock>
        </>
      ) : null}
    </div>
  );
}

function PickableBlock({ selecting, checked, onToggle, label, children }) {
  return (
    <div className={`service-order-product-sheet__pick${selecting ? " is-selecting" : ""}`}>
      {selecting ? (
        <input
          type="checkbox"
          className="service-order-product-sheet__check"
          checked={Boolean(checked)}
          onChange={onToggle}
          aria-label={label}
        />
      ) : null}
      <div className="service-order-product-sheet__pick-body">{children}</div>
    </div>
  );
}
